import { useCallback, useEffect, useRef, useState } from 'react';
import { isAuthStatus, type AuthRevalidationFn } from '@/lib/auth';
import { isLiveMarketSession } from '@/lib/sessionDate';
import type { ExposureType, Ticker } from '@/store/heatmapStore';
import type { SessionAnalytics, SessionTimeframe } from '@/types/session';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface UseSessionAnalyticsResult {
  analytics: SessionAnalytics | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

const SESSION_ANALYTICS_RETRY_MS = 60_000;
const SESSION_ANALYTICS_REFRESH_MS = 60_000;

export function useSessionAnalytics({
  ticker,
  type,
  sessionDate,
  timeframe,
  enabled,
  onAuthRevalidationRequested,
}: {
  ticker: Ticker;
  type: ExposureType;
  sessionDate: string;
  timeframe: SessionTimeframe;
  enabled: boolean;
  onAuthRevalidationRequested: AuthRevalidationFn;
}): UseSessionAnalyticsResult {
  const [analytics, setAnalytics] = useState<SessionAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const analyticsRef = useRef<SessionAnalytics | null>(null);
  const lastSuccessfulRequestRef = useRef<{ key: string; timestamp: number } | null>(null);
  const retry = useCallback(() => setRequestVersion((version) => version + 1), []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const abortController = new AbortController();
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let refreshRequested = false;
    const requestKey = `${ticker}:${type}:${sessionDate}:${timeframe}`;
    const existingAnalytics = analyticsRef.current;
    const isBackgroundRefresh = matchesSelection(existingAnalytics, {
      ticker,
      type,
      sessionDate,
      timeframe,
    });
    const needsCloseTransition = () =>
      matchesSelection(analyticsRef.current, { ticker, type, sessionDate, timeframe }) &&
      analyticsRef.current?.priceLabel === 'Current Price' &&
      !isLiveMarketSession(sessionDate);
    const requestRefresh = () => {
      if (refreshRequested) {
        return;
      }
      refreshRequested = true;
      retry();
    };
    const scheduleRefresh = (delay: number, periodic: boolean = false) => {
      retryTimeout = setTimeout(() => {
        if (periodic) {
          if (
            document.visibilityState === 'hidden' ||
            (!isLiveMarketSession(sessionDate) && !needsCloseTransition())
          ) {
            return;
          }
        }
        requestRefresh();
      }, delay);
    };
    const refreshStaleVisibleSession = () => {
      const lastSuccessfulRequest = lastSuccessfulRequestRef.current;
      if (
        document.visibilityState !== 'hidden' &&
        lastSuccessfulRequest?.key === requestKey &&
        (needsCloseTransition() ||
          (isLiveMarketSession(sessionDate) &&
            Date.now() - lastSuccessfulRequest.timestamp >= SESSION_ANALYTICS_REFRESH_MS))
      ) {
        requestRefresh();
      }
    };

    window.addEventListener('focus', refreshStaleVisibleSession);
    document.addEventListener('visibilitychange', refreshStaleVisibleSession);

    void (async () => {
      try {
        setIsLoading(!isBackgroundRefresh);
        setError(null);
        if (!isBackgroundRefresh) {
          analyticsRef.current = null;
          setAnalytics(null);
        }

        const params = new URLSearchParams({
          type,
          date: sessionDate,
          timeframe,
        });
        const response = await fetch(`/api/session/${ticker}?${params.toString()}`, {
          signal: abortController.signal,
        });
        const json = (await response.json()) as ApiResponse<SessionAnalytics>;

        if (isAuthStatus(response.status)) {
          await onAuthRevalidationRequested('background');
        }

        if (!response.ok || !json.success || json.data == null) {
          throw new Error(json.error ?? 'Failed to load session analytics');
        }

        analyticsRef.current = json.data;
        lastSuccessfulRequestRef.current = { key: requestKey, timestamp: Date.now() };
        setAnalytics(json.data);
        if (hasRetryableFailure(json.data)) {
          scheduleRefresh(SESSION_ANALYTICS_RETRY_MS);
        } else if (isLiveMarketSession(sessionDate)) {
          scheduleRefresh(SESSION_ANALYTICS_REFRESH_MS, true);
        }
      } catch (err) {
        if (abortController.signal.aborted) {
          return;
        }

        const message = err instanceof Error ? err.message : 'Failed to load session analytics';
        if (!isBackgroundRefresh) {
          analyticsRef.current = null;
          setAnalytics(null);
        }
        setError(message);
        scheduleRefresh(SESSION_ANALYTICS_RETRY_MS);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
      window.removeEventListener('focus', refreshStaleVisibleSession);
      document.removeEventListener('visibilitychange', refreshStaleVisibleSession);
      if (retryTimeout != null) {
        clearTimeout(retryTimeout);
      }
    };
  }, [
    enabled,
    onAuthRevalidationRequested,
    requestVersion,
    retry,
    sessionDate,
    timeframe,
    ticker,
    type,
  ]);

  const currentAnalytics =
    enabled &&
    analytics != null &&
    analytics.ticker === ticker &&
    analytics.exposureType === type &&
    analytics.sessionDate === sessionDate &&
    analytics.timeframe === timeframe
      ? analytics
      : null;

  return {
    analytics: currentAnalytics,
    isLoading: enabled ? isLoading : false,
    error: enabled ? error : null,
    retry,
  };
}

function matchesSelection(
  analytics: SessionAnalytics | null,
  selection: {
    ticker: Ticker;
    type: ExposureType;
    sessionDate: string;
    timeframe: SessionTimeframe;
  },
): boolean {
  return (
    analytics != null &&
    analytics.ticker === selection.ticker &&
    analytics.exposureType === selection.type &&
    analytics.sessionDate === selection.sessionDate &&
    analytics.timeframe === selection.timeframe
  );
}

function hasRetryableFailure(analytics: SessionAnalytics): boolean {
  return (
    analytics.dataAvailability.priceBars.status === 'unavailable' ||
    analytics.dataAvailability.openClose.status === 'unavailable'
  );
}
